import React, { useState } from 'react';
import { isTokenExpiringSoon } from './tokenUtils';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AdminSideMenu from './AdminSideMenu';
import AuditScreen from './AuditScreen';
import AuditLogScreen from './AuditLogScreen';
import Header from './Header';
import MemberDashboard from './MemberDashboard';
import EditMemberForm from './EditMemberForm';
import AddMemberForm from './AddMemberForm';
import SportMembers from './SportMembers';
import GradeManagement from './GradeManagement';
import SportRoleScreen from './SportRoleScreen';
import SportManagement from './SportManagement';
import Login from './Login';
import MemberScreen from './MemberScreen';

import PasswordResetScreen from './PasswordResetScreen';

import SettingsTable from './SettingsTable';

import MailPage from './MailPage';

import MailLogScreen from './MailLogScreen';

import type { Member } from './EditMemberForm';

const API_BASE_URL = '/api';

function App() {
  const [adminSection, setAdminSection] = useState('members');
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isMemberLoggedIn, setIsMemberLoggedIn] = useState(false);
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [serverAddError, setServerAddError] = useState('');
  const [serverEditError, setServerEditError] = useState('');
  const [userSports, setUserSports] = useState<string[]>([]);

  // Helper function to extract sports from sports_admin roles
  function getUserSports(roles: string[] | undefined): string[] {
    if (!roles || !Array.isArray(roles)) return [];
    return roles
      .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
      .map(r => r.replace('sports_admin_', '').toLowerCase());
  }


  // Helper to refresh token if expiring soon
  const refreshTokenIfNeeded = async (token: string | null): Promise<string | null> => {
    if (!token) return null;
    if (!isTokenExpiringSoon(token, 600)) return token;
    try {
      const res = await fetch(`${API_BASE_URL}/oauth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        setAccessToken(data.access_token);
        return data.access_token;
      }
    } catch {}
    return token;
  };

  const handleLogin = (user: Member, token: string) => {
    setAccessToken(token);
    setMemberInfo(user);
    const roles = user.securityRoles || [];
    const isAdmin = hasRole(roles, 'admin');
    const isAuthorized = isAdmin || hasRole(roles, 'trustee') || roles.some(r => r.startsWith('sports_admin_'));
    setIsAdminLoggedIn(isAuthorized);
    setIsMemberLoggedIn(!isAuthorized);
    setUserSports(getUserSports(roles));
  };

  const handleLogout = () => {
    setAccessToken(null);
    setMemberInfo(null);
    setIsAdminLoggedIn(false);
    setIsMemberLoggedIn(false);
  };

  // Wrap setEditingMember for correct type
  const handleEditMember = (member: Member) => setEditingMember(member);

  function hasRole(roles: string[] | undefined, role: string): boolean {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
  }

  // Login handler — Login component now does the OAuth call and passes user+token
  const handleLoginDirect = (user: { id: number; name: string; email: string; securityRoles?: string[] }, token: string) => {
    const roles = user.securityRoles || [];
    handleLogin({
      id: user.id,
      name: user.name,
      email: user.email,
      securityRoles: roles.map(r => r.toLowerCase()),
      credits: 0,
    }, token);
  };

  // Determine if user can add members
  function canAddMember(roles: string[] | undefined): boolean {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.map(r => r.toLowerCase()).includes('admin') ||
           roles.map(r => r.toLowerCase()).includes('trustee');
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gray-100">
        <Header
          isAdminLoggedIn={isAdminLoggedIn}
          isMemberLoggedIn={isMemberLoggedIn}
          onAdminLogout={handleLogout}
          onMemberLogout={handleLogout}
        />
        <Routes>
          <Route path="/reset-password/:memberId" element={<PasswordResetScreen currentUser={memberInfo ? { id: memberInfo.id, securityRoles: memberInfo.securityRoles } : null} accessToken={accessToken} />} />
          <Route path="*" element={
            <>
              {!isAdminLoggedIn && !isMemberLoggedIn && (
                <Login onLogin={handleLoginDirect} />
              )}
              {isAdminLoggedIn && (
                <div className="flex w-full min-h-screen">
                  <AdminSideMenu selected={adminSection} onSelect={setAdminSection} userRoles={memberInfo?.securityRoles} />
                  <main className="flex-1 glass-card p-8 rounded-b-2xl shadow-2xl mt-0 border-t-0 border-blue-200 ml-56">
                    {adminSection === 'members' && (
                      showAddMember ? (
                        <AddMemberForm
                          onAdd={async (memberData) => {
                            setServerAddError('');
                            setAddMemberLoading(true);
                            try {
                              const token = await refreshTokenIfNeeded(accessToken);
                              const res = await fetch(`${API_BASE_URL}/members`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                                },
                                body: JSON.stringify(memberData),
                              });
                              const data = await res.json();
                              if (res.ok) {
                                setShowAddMember(false);
                                setAddMemberLoading(false);
                              } else {
                                setServerAddError(data.message || 'Failed to add member');
                                setAddMemberLoading(false);
                              }
                            } catch (err) {
                              setServerAddError('Network error or API is not running.');
                              setAddMemberLoading(false);
                            }
                          }}
                          onCancel={() => setShowAddMember(false)}
                          loading={addMemberLoading}
                          accessToken={accessToken}
                          serverError={serverAddError}

                        />
                      ) : editingMember ? (
                        <EditMemberForm
                          member={editingMember}
                          onCancel={() => setEditingMember(null)}
                          accessToken={accessToken}
                          onSave={async (formData, sportsRoles) => {
                            setServerEditError('');
                            setAddMemberLoading(true);
                            try {
                              const token = await refreshTokenIfNeeded(accessToken);
                              // Prepare payload for update
                              const payload = {
                                ...formData,
                                // Keep roles as string array (from form), send sport roles separately
                                sportsRoles,
                              };
                              const res = await fetch(`${API_BASE_URL}/members/${formData.id}`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                                },
                                body: JSON.stringify(payload),
                              });
                              const data = await res.json();
                              if (res.ok) {
                                setEditingMember(null);
                              } else {
                                setServerEditError(data.message || 'Failed to update member');
                              }
                            } catch (err) {
                              setServerEditError('Network error or API is not running.');
                            } finally {
                              setAddMemberLoading(false);
                            }
                          }}
                          serverError={serverEditError}
                          userRoles={memberInfo?.securityRoles}
                        />
                      ) : (
                        <MemberDashboard
                          apiBaseUrl={API_BASE_URL}
                          accessToken={accessToken}
                          onAddMember={() => { setServerAddError(''); setShowAddMember(true); }}
                          onEditMember={handleEditMember}
                          onLogout={handleLogout}
                          loggedInUserId={memberInfo?.id}
                          userRoles={memberInfo?.securityRoles}
                          userSports={userSports}
                        />
                      )
                    )}
                    {adminSection === 'sport' && (
                      <SportManagement apiBaseUrl={API_BASE_URL} accessToken={accessToken} refreshKey={adminSection} userRoles={memberInfo?.securityRoles} userSports={userSports} />
                    )}
                    {adminSection === 'sportmembers' && (
                      <SportMembers apiBaseUrl={API_BASE_URL} accessToken={accessToken} userRoles={memberInfo?.securityRoles} userSports={userSports} />
                    )}
                    {adminSection === 'grades' && (
                      <GradeManagement apiBaseUrl={API_BASE_URL} accessToken={accessToken} userRoles={memberInfo?.securityRoles} userSports={userSports} />
                    )}
                    {adminSection === 'sportroles' && (
                      <SportRoleScreen apiBaseUrl={API_BASE_URL} accessToken={accessToken} userRoles={memberInfo?.securityRoles} userSports={userSports} />
                    )}
                    {adminSection === 'settings' && (
                      <SettingsTable apiBaseUrl={API_BASE_URL} accessToken={accessToken} />
                    )}
                    {adminSection === 'mail' && (
                      <MailPage accessToken={accessToken || undefined} userRoles={memberInfo?.securityRoles} />
                    )}
                    {adminSection === 'auditlog' && hasRole(memberInfo?.securityRoles, 'admin') && (
                      <AuditLogScreen apiBaseUrl={API_BASE_URL} accessToken={accessToken} />
                    )}
                    {adminSection === 'auditlogin' && hasRole(memberInfo?.securityRoles, 'admin') && (
                      <AuditScreen apiBaseUrl={API_BASE_URL} accessToken={accessToken} />
                    )}
                    {adminSection === 'auditlogin' && !hasRole(memberInfo?.securityRoles, 'admin') && (
                      <div className="text-red-600">You do not have access to view the audit login log.</div>
                    )}
                    {adminSection === 'maillog' && (
                      <MailLogScreen apiBaseUrl={API_BASE_URL} accessToken={accessToken} />
                    )}
                  </main>
                </div>
              )}
              {isMemberLoggedIn && memberInfo && !isAdminLoggedIn && (
                <MemberScreen member={memberInfo} onLogout={handleLogout} accessToken={accessToken} />
              )}
            </>
          } />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
